const TitleSearchIndex = {
  products: null,
  loading: null,

  async load(signal) {
    if (this.products) {
      return this.products;
    }
    if (this.loading) {
      return this.loading;
    }

    this.loading = this.fetchAllProducts(signal)
      .then((products) => {
        this.products = products;
        this.loading = null;
        return products;
      })
      .catch((error) => {
        this.loading = null;
        throw error;
      });

    return this.loading;
  },

  async fetchAllProducts(signal) {
    const products = [];
    let page = 1;

    while (page <= 8) {
      const response = await fetch(
        `${window.shopUrl}/products.json?limit=250&page=${page}`,
        { signal },
      );
      if (!response.ok) {
        break;
      }
      const data = await response.json();
      const batch = data.products || [];
      if (!batch.length) {
        break;
      }
      products.push(...batch);
      if (batch.length < 250) {
        break;
      }
      page += 1;
    }

    return products;
  },

  async findByTitle(query, limit = 10, signal) {
    const normalized = query.trim().toLowerCase();
    if (normalized.length < 2) {
      return [];
    }

    const products = await this.load(signal);
    const matches = [];

    for (const product of products) {
      if ((product.title || "").toLowerCase().includes(normalized)) {
        matches.push(product);
        if (matches.length >= limit) {
          break;
        }
      }
    }

    return matches;
  },

  formatMoney(price) {
    const value =
      typeof price === "string" ? parseFloat(price) : Number(price || 0);
    if (Number.isNaN(value)) {
      return "";
    }
    const amount = value.toFixed(2).replace(".", ",");
    const format = FoxTheme?.settings?.moneyFormat || "€{{amount}}";
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(value)));
  },

  productUrl(product) {
    return `${window.shopUrl}/products/${product.handle}`;
  },

  imageUrl(product) {
    const image = product.images?.[0] || product.featured_image;
    if (!image) {
      return "";
    }
    if (typeof image === "string") {
      return image.startsWith("//") ? `https:${image}` : image;
    }
    const src = image.src || "";
    return src.startsWith("//") ? `https:${src}` : src;
  },

  renderPredictiveHtml(products, query, seeAllLabel) {
    const items = products
      .map((product, index) => {
        const image = this.imageUrl(product);
        const price = product.variants?.[0]?.price;
        const imageHtml = image
          ? `<a href="${this.productUrl(product)}" class="media-wrapper blocks-radius-sm">
              <img src="${image}" alt="" loading="lazy" width="100" height="100">
            </a>`
          : "";
        const priceHtml =
          price != null
            ? `<div class="f-price"><span class="f-price-item f-price-item--regular">${this.formatMoney(price)}</span></div>`
            : "";

        return `<li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <div class="flex items-center gap-3">
            ${imageHtml}
            <div class="grid flex-1">
              <p class="block m-0">
                <a href="${this.productUrl(product)}" class="reversed-link text-pcard-title">${product.title}</a>
              </p>
              ${priceHtml}
            </div>
          </div>
        </li>`;
      })
      .join("");

    const label = (seeAllLabel || "Search for __Q__").replace("__Q__", query);

    return `<div class="grid gap-6 w-full">
      <div class="grid gap-3 predictive-search-result search__box-item predictive-search-result--products">
        <div class="grid">
          <ul id="predictive-search-results-products-list" class="grid gap-4" role="group" aria-labelledby="predictive-search-products">
            ${items}
          </ul>
        </div>
      </div>
    </div>
    <div class="search__results-all block">
      <button type="submit" class="reversed-link btn--inherit flex items-center justify-center gap-2 font-body-bolder">
        ${label}
      </button>
    </div>`;
  },

  renderGridHtml(products) {
    return `<grid-list id="ProductsList" class="grid-list products-list f-grid md:f-grid-3-cols lg:f-grid-4-cols xl:f-grid-4-cols f-grid-2-cols f-grid--gap-medium" data-result-count="${products.length}">
      ${products
        .map((product) => {
          const image = this.imageUrl(product);
          const price = product.variants?.[0]?.price;
          const imageHtml = image
            ? `<a href="${this.productUrl(product)}" class="product-card__media media-wrapper">
                <img src="${image}" alt="" loading="lazy" width="400" height="400">
              </a>`
            : "";
          const priceHtml =
            price != null
              ? `<div class="f-price"><span class="f-price-item f-price-item--regular">${this.formatMoney(price)}</span></div>`
              : "";

          return `<div class="f-column card">
            <div class="product-card product-card-style-card">
              <div class="product-card__wrapper h-full">
                ${imageHtml}
                <div class="product-card__info">
                  <h3 class="product-card__title text-pcard-title">
                    <a href="${this.productUrl(product)}">${product.title}</a>
                  </h3>
                  ${priceHtml}
                </div>
              </div>
            </div>
          </div>`;
        })
        .join("")}
    </grid-list>`;
  },
};

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener("click", this.clear.bind(this));
    this.input.addEventListener(
      "input",
      FoxTheme.utils.debounce(this.onChange.bind(this), 300),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    document.addEventListener("pointerdown", this.handlePointerDown.bind(this));
    this.searchContent = this.querySelector(".search__content");
    this.searchRecommendationEmpty =
      this.dataset.searchRecommendationEmpty === "true";

    const isTemplateSearch = this.closest(".template-search");
    if (isTemplateSearch) {
      this.handleClickOutside = this.handleClickOutside.bind(this);
      document.addEventListener("pointerdown", this.handleClickOutside);
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get sectionId() {
    return this.dataset.sectionId || FoxTheme.utils.getSectionId(this);
  }
  get resultsContainerId() {
    return "PredictiveSearchResults-" + this.sectionId;
  }
  get resultsLimit() {
    return parseInt(this.dataset.resultsLimit || "3", 10);
  }

  onFocus() {
    if (this.closest(".template-search")) {
      document.body.classList.add("predictive-search-open");
      if (this.getQuery().length === 0) {
        return;
      }
      this.renderSection(this.setupSearchURL().toString());
    }
  }

  handlePointerDown(event) {
    const path = event.composedPath?.() || [];

    if (path.includes(this)) {
      return;
    }

    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add("hidden");
    }

    this.input.value = "";
    this.removeAttribute("results");
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event && event.preventDefault();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add("hidden");
    }
    this.input.value = "";
    this.input.focus();
    this.removeAttribute("results");
  }

  setupSearchURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.search_url}`);
    url.searchParams.set("q", this.getQuery());
    url.searchParams.set("type", "product");
    url.searchParams.set("options[prefix]", "last");
    url.searchParams.set("section_id", this.sectionId);
    return url;
  }

  setupPredictiveURL() {
    const url = new URL(
      `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`,
    );
    url.searchParams.set("q", this.getQuery());
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("section_id", this.sectionId);
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.renderSection(this.setupSearchURL().toString());
  }

  responseHasResults(responseText) {
    const parsedDoc = new DOMParser().parseFromString(
      responseText,
      "text/html",
    );
    const contentElement = parsedDoc.getElementById(this.resultsContainerId);
    return (
      contentElement?.querySelector(".predictive-search__list-item") !== null
    );
  }

  renderSection(url) {
    const query = this.getQuery();
    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url, query);
      return;
    }
    this.renderSectionFromFetch(url, query);
  }

  renderSectionFromCache(url, query) {
    if (this.getQuery() !== query) {
      return;
    }
    this.renderSearchResults(this.cachedMap.get(url), query);
    this.setAttribute("results", "");
  }

  fetchSection(url, signal) {
    return fetch(url, { signal }).then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.text();
    });
  }

  resolveSectionResponse(searchUrl, query, signal) {
    if (this.cachedMap.has(searchUrl)) {
      return Promise.resolve(this.cachedMap.get(searchUrl));
    }

    return this.fetchSection(searchUrl, signal).then((searchText) => {
      if (this.responseHasResults(searchText)) {
        this.cachedMap.set(searchUrl, searchText);
        return searchText;
      }

      const suggestUrl = this.setupPredictiveURL().toString();
      if (this.cachedMap.has(suggestUrl)) {
        const suggestText = this.cachedMap.get(suggestUrl);
        if (this.responseHasResults(suggestText)) {
          this.cachedMap.set(searchUrl, suggestText);
          return suggestText;
        }
      }

      return this.fetchSection(suggestUrl, signal).then((suggestText) => {
        this.cachedMap.set(suggestUrl, suggestText);
        if (this.responseHasResults(suggestText)) {
          this.cachedMap.set(searchUrl, suggestText);
          return suggestText;
        }

        return TitleSearchIndex.findByTitle(
          query,
          this.resultsLimit,
          signal,
        ).then((products) => {
          if (!products.length) {
            this.cachedMap.set(searchUrl, searchText);
            return searchText;
          }

          const html = this.wrapTitleResults(
            TitleSearchIndex.renderPredictiveHtml(
              products,
              query,
              this.dataset.seeAllLabel,
            ),
          );
          this.cachedMap.set(searchUrl, html);
          return html;
        });
      });
    });
  }

  wrapTitleResults(innerHtml) {
    return `<div id="${this.resultsContainerId}">${innerHtml}</div>`;
  }

  renderSectionFromFetch(url, query) {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setAttribute("loading", "true");
    this.resetButton.classList.add("btn--loading");

    this.resolveSectionResponse(url, query, signal)
      .then((responseText) => {
        if (this.getQuery() !== query) {
          return;
        }
        this.renderSearchResults(responseText, query);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching data: ", error);
        this.setAttribute("error", "Failed to load data");
      })
      .finally(() => {
        if (this.getQuery() !== query) {
          return;
        }
        this.removeAttribute("loading");
        this.resetButton.classList.remove("btn--loading");
        this.setAttribute("results", "true");
      });
  }

  renderSearchResults(responseText, query) {
    const targetElement = document.getElementById(this.resultsContainerId);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, "text/html");
      const contentElement = parsedDoc.getElementById(this.resultsContainerId);

      if (contentElement) {
        this.searchContent?.classList.remove("hidden");
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(
          `Element with id '${this.resultsContainerId}' not found in the parsed response.`,
        );
      }
    } else {
      console.error(
        `Element with id '${this.resultsContainerId}' not found in the document.`,
      );
    }
  }

  handleClickOutside(event) {
    const path = event.composedPath?.() || [];

    if (!path.includes(this)) {
      requestAnimationFrame(() => {
        document.body.classList.remove("predictive-search-open");
      });
    }
  }
}
customElements.define("predictive-search", PredictiveSearch, {
  extends: "form",
});

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define("search-drawer", SearchDrawer);

class SearchTitleFallback {
  constructor() {
    this.container = document.getElementById("ProductGridContainer");
    if (!this.container?.hasAttribute("data-title-search-fallback")) {
      return;
    }

    this.query = this.container.dataset.query;
    this.limit = parseInt(this.container.dataset.limit || "16", 10);
    if (!this.query) {
      return;
    }

    this.load();
  }

  load() {
    TitleSearchIndex.findByTitle(this.query, this.limit)
      .then((products) => {
        if (!products.length) {
          return;
        }

        this.container.innerHTML = TitleSearchIndex.renderGridHtml(products);
        this.container.removeAttribute("data-title-search-fallback");
        this.container
          .closest(".template-search")
          ?.classList.remove("template-search--empty");

        const productCount = document.getElementById("ProductCount");
        if (productCount) {
          productCount.textContent = productCount.textContent.replace(
            /\d+/,
            String(products.length),
          );
        }
      })
      .catch((error) => {
        console.error("Title search fallback failed: ", error);
      });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new SearchTitleFallback();
});
